import mixpanel from 'mixpanel-browser'
import posthog from 'posthog-js'

import config from 'lib/config'
import logError from 'lib/logError'
import { logger } from 'lib/utils/logger'

export const initializeMixpanel = () => {
  config.MIXPANEL && mixpanel.init(config.MIXPANEL)
}

const trackEvent = (module, eventName, payload = {}, hideToast = false) => {
  if (config.IS_TEST_MODE) {
    return
  }

  payload = {
    ...window?.globals?._logData,
    ...payload,
  }
  config.DEBUG_MODE && logger('TRACKEVENT:', { module, eventName, payload })
  // if (config.ENV === 'development') return
  const showLogs = !hideToast && config.DEBUG_MODE
  try {
    if (!Object.keys(trackEvent.modules).includes(module)) {
      showLogs &&
        window?.globals?.service?.toast(
          'TRACKEVENT: module not part of trackEvent.modules: ' + eventName,
          { type: 'error' }
        )
      return window.console.error(
        'TRACKEVENT: module not part of trackEvent.modules',
        eventName
      )
    }
    if (!Object.keys(trackEvent.events).includes(eventName)) {
      logger(
        'Object.keys(trackEvent.events)',
        Object.keys(trackEvent.events),
        eventName
      )
      showLogs &&
        window?.globals?.service?.toast(
          'TRACKEVENT: eventName not part of trackEvent.events: ' + eventName,
          { type: 'error' }
        )
      return window.console.error(
        'TRACKEVENT: eventName not part of trackEvent.eventName',
        eventName
      )
    }

    if (config.MIXPANEL) {
      mixpanel?.track?.(eventName, payload)
    }
    if (config.POSTHOG_API_KEY) {
      posthog.capture(eventName, {
        ...payload,
        module,
      })
    }

    //GA4
    window.dataLayer = window?.dataLayer || []
    window.dataLayer.push({
      event: eventName,
      ...payload,
      module,
    })

    showLogs && window?.globals?.service?.toast(eventName)
  } catch (error) {
    logError(error, 'trackEvent', {
      eventName,
      module,
    })
  }
}

export const trackPH = (module, eventName, payload) => {
  if (config.IS_TEST_MODE) {
    return
  }
  try {
    config.DEBUG_MODE &&
      logger('TRACKPH:', eventName, {
        module,
        payload: {
          ...window?.globals?._logData,
          ...payload,
        },
      })
  } catch (e) {
    logError(e, 'trackPH', {
      eventName,
      module,
    })
  }
}

trackEvent.modules = {
  auth: 'auth',
  chat: 'chat',
  dock: 'dock',
  header: 'header',
  queue: 'queue',
  quickfind: 'quickfind',
  service: 'service',
  socket: 'socket',
  fetcher: 'fetcher',
  redux: 'redux',
  settings: 'settings',
  ctwa: 'ctwa',
  createOrg: 'createOrg',
  integrations: 'integrations',
  linkCreator: 'linkCreator',
  enigmaSales: 'enigmaSales',
  apps: 'apps',
  onBoardingFlow: 'onBoardingFlow',
  guru: 'guru',
  catalogManager: 'catalogManager',
  billing: 'billing',
  flowLibrary: 'flowLibrary',
  flowCanvas: 'flowCanvas',
  navbar: 'navbar',
  realtime: 'realtime',
  admin: 'admin',
  sessionBlocker: 'sessionBlocker',
  hub: 'hub',
}

trackPH.modules = trackEvent.modules

/**
 * component_action
 * e.g: conversation_click
 **/
trackEvent.events = {
  conversation_filter_change: 'conversation_filter_change',
  conversation_filter_clear: 'conversation_filter_clear',
  chatwindow_header_name_click: 'chatwindow_header_name_click',
  chatreply_quick_option_change: 'chatreply_quick_option_change',
  chatreply_quick_template_select: 'chatreply_quick_template_select',
  chatreply_quick_reply_select: 'chatreply_quick_reply_select',
  chatreply_expiry_template_select: 'chatreply_expiry_template_select',
  chatreply_private_note_click: 'chatreply_private_note_click',
  chatreply_file_upload: 'chatreply_file_upload',
  chatreply_file_upload_complete: 'chatreply_file_upload_complete',
  chatreply_attachment_delete: 'chatreply_attachment_delete',
  chatreply_emoji: 'chatreply_emoji',
  chatreply_emoji_toggle: 'chatreply_emoji_toggle',
  chatreply_send_blocked: 'chatreply_send_blocked',
  chatreply_audio_upload_audio: 'chatreply_audio_upload_audio',
  chatreply_audio_action: 'chatreply_audio_action',
  template_edit_post_click: 'template_edit_post_click',
  template_edit_cancel_click: 'template_edit_cancel_click',
  template_modal_close: 'template_modal_close',
  template_advanced_option_toggle: 'template_advanced_option_toggle',
  conversation_list_refresh_toast: 'conversation_list_refresh_toast',
  enigma_take_over_by_agent: 'enigma_take_over_by_agent',
  mobile_app_link_click: 'mobile_app_link_click',

  // DOCK:START
  dock_click_dock_tab: 'dock_click_dock_tab',
  dock_customer_profile_add_address: 'dock_customer_profile_add_address',
  dock_customer_profile_add_new_tag: 'dock_customer_profile_add_new_tag',
  dock_customer_profile_edit_name: 'dock_customer_profile_edit_name',
  dock_customer_profile_edit_name_cancel:
    'dock_customer_profile_edit_name_cancel',
  dock_customer_profile_edit_name_save: 'dock_customer_profile_edit_name_save',
  dock_customer_profile_sections_click: 'dock_customer_profile_sections_click',
  dock_customer_profile_media_tabs_click:
    'dock_customer_profile_media_tabs_click',
  dock_customer_profile_media_click: 'dock_customer_profile_media_click',
  dock_tags_sections_click: 'dock_tags_sections_click',
  dock_tags_add_new_tag: 'dock_tags_add_new_tag',
  dock_tags_delete_tag: 'dock_tags_delete_tag',
  dock_tags_add_new_tag_tag_click: 'dock_tags_add_new_tag_tag_click',
  dock_tags_add_new_tag_button_click: 'dock_tags_add_new_tag_button_click',
  dock_orders_search: 'dock_orders_search',
  dock_orders_tab_change: 'dock_orders_tab_change',
  dock_orders_card_toggle: 'dock_orders_card_toggle',
  dock_orders_action_click: 'dock_orders_action_click',
  dock_products_cart_open: 'dock_products_cart_open',
  dock_products_cart_close: 'dock_products_cart_close',
  dock_products_cart_update: 'dock_products_cart_update',
  dock_products_cart_send_to_chat_click:
    'dock_products_cart_send_to_chat_click',
  dock_products_cart_checkout_click: 'dock_products_cart_checkout_click',
  dock_products_cart_checkout_close: 'dock_products_cart_checkout_close',
  dock_products_cart_checkout_order_summary_toggle:
    'dock_products_cart_checkout_order_summary_toggle',
  dock_products_cart_checkout_send_to_chat_click:
    'dock_products_cart_checkout_send_to_chat_click',
  dock_products_cart_checkout_process_order_click:
    'dock_products_cart_checkout_process_order_click',
  dock_products_cart_checkout_add_address_click:
    'dock_products_cart_checkout_add_address_click',
  dock_products_cart_checkout_select_address:
    'dock_products_cart_checkout_select_address',
  dock_products_cart_checkout_change_address:
    'dock_products_cart_checkout_change_address',
  dock_products_list_search: 'dock_products_list_search',
  dock_products_list_filter_toggle: 'dock_products_list_filter_toggle',
  dock_products_list_filter_update: 'dock_products_list_filter_update',
  dock_products_list_product_info_click:
    'dock_products_list_product_info_click',
  dock_products_list_add_to_cart_click: 'dock_products_list_add_to_cart_click',
  dock_products_list_send_to_chat_click:
    'dock_products_list_send_to_chat_click',
  dock_products_product_info_send_to_chat_click:
    'dock_products_product_info_send_to_chat_click',
  dock_products_product_info_add_to_cart_click:
    'dock_products_product_info_add_to_cart_click',
  dock_products_product_info_filter_update:
    'dock_products_product_info_filter_update',
  dock_products_product_info_gallery_send_to_chat:
    'dock_products_product_info_gallery_send_to_chat',
  dock_catalog_expand_click: 'dock_catalog_expand_click',
  dock_bottom_expand_click: 'dock_bottom_expand_click',
  // DOCK:END

  storelist_store_switch: 'storelist_store_switch',
  maintabs_tab_change: 'maintabs_tab_change',

  agentteamlist_search: 'agentteamlist_search',
  agentteamlist_tab_change: 'agentteamlist_tab_change',
  agentteamlist_assign_change: 'agentteamlist_assign_change',
  queuelist_select_conversation: 'queuelist_select_conversation',
  queuelist_close_chat: 'queuelist_close_chat',
  chatpreview_goto_chat: 'chatpreview_goto_chat',
  queuelist_toggle_chatpreview: 'queuelist_toggle_chatpreview',
  chatwindowassign_previewmode_change: 'chatwindowassign_previewmode_change',
  queue_refresh_list_toast: 'queue_refresh_list_toast',

  api_call: 'api_call',
  redux_action: 'redux_action',
  header_store_info_click: 'header_store_info_click',
  header_mobile_menu_click: 'header_mobile_menu_click',

  internet_offline_toast: 'internet_offline_toast',
  sleep_toast: 'sleep_toast',

  // settings
  shopify_merger: 'shopify_merger',
  shopify_merger_mismatch: 'shopify_merger_mismatch',
  // service
  app_crash: 'app_crash',
  shown_health_banner: 'shown_health_banner',

  // annoucement

  show_woo_kill_alert_2023: 'show_woo_kill_alert_2023',
  stop_woo_kill_alert_2023: 'stop_woo_kill_alert_2023',
  accept_woo_kill_alert_2023: 'accept_woo_kill_alert_2023',

  show_gpt_pay_alert_2023: 'show_gpt_pay_alert_2023',
  stop_gpt_pay_alert_2023: 'stop_gpt_pay_alert_2023',
  accept_gpt_pay_alert_2023: 'accept_gpt_pay_alert_2023',

  show_catalog_alert_2023: 'show_catalog_alert_2023',
  stop_catalog_alert_2023: 'stop_catalog_alert_2023',
  accept_catalog_alert_2023: 'accept_catalog_alert_2023',

  show_pricing_alert_2023: 'show_pricing_alert_2023',
  stop_subscription_2023: 'stop_subscription_2023',
  accept_subscription_2023: 'accept_subscription_2023',

  enable_cod_button: 'enable_cod_button',
  ignore_cod_button: 'ignore_cod_button',
  show_cod_news: 'show_cod_news',

  enable_seg_button: 'enable_seg_button',
  ignore_seg_button: 'ignore_seg_button',
  show_seg_news: 'show_seg_news',

  enable_blling_alert_button: 'enable_blling_alert_button',
  ignore_blling_alert_button: 'ignore_blling_alert_button',
  show_blling_alert_news: 'show_blling_alert_news',

  show_sync_news: 'show_sync_news',
  ignore_sync_button: 'ignore_sync_button',

  enable_broadcast_button: 'enable_broadcast_button',
  show_broadcast_news: 'show_broadcast_news',
  ignore_broadcast_button: 'ignore_broadcast_button',
  learn_more_broadcast_button: 'learn_more_broadcast_button',

  down_time_sept_show: 'down_time_sept_show',
  down_time_sept_accept: 'down_time_sept_accept',
  down_time_sept_ignore: 'down_time_sept_ignore',

  show_wismo_launch: 'show_wismo_launch',
  accept_wismo_launch: 'accept_wismo_launch',
  stop_wismo_launch: 'stop_wismo_launch',

  show_broadcast_banner: 'show_broadcast_banner',
  broadcast_learn_more_click: 'broadcast_learn_more_click',
  broadcast_not_interested_click: 'broadcast_not_interested_click',

  pricing_2024_show_banner: 'pricing_2024_show_banner',
  pricing_2024_learn_more: 'pricing_2024_learn_more',
  pricing_2024_not_interested_click: 'pricing_2024_not_interested_click',

  catalog_2024_july_show_banner: 'catalog_2024_july_show_banner',
  catalog_2024_july_learn_more: 'catalog_2024_july_learn_more',
  catalog_2024_july_not_interested_click:
    'catalog_2024_july_not_interested_click',

  broadcast_2024_oct_show_banner: 'broadcast_2024_oct_show_banner',
  broadcast_2024_oct_learn_more: 'broadcast_2024_oct_learn_more',
  broadcast_2024_oct_not_interested_click:
    'broadcast_2024_oct_not_interested_click',

  // login - secondaryAccount events
  log_in: 'log_in',
  log_out: 'log_out',

  // checkout - scondaryAccount events
  checkout_sent: 'checkout_sent',

  //ctwa
  ctwa_list_open: 'ctwa_list_open',
  ctwa_overview_open: 'ctwa_overview_open',
  ctwa_overview_datatab_open: 'ctwa_overview_datatab_open',
  ctwa_overview_export_click: 'ctwa_overview_export_click',

  //
  chatpowers_link_click: 'chatpowers_link_click',

  // createOrg
  createOrg_submit_click: 'createOrg_submit_click',

  //onboarding qr
  onboarding_qr_shown: 'onboarding_qr_shown',
  onboarding_qr_scanned: 'onboarding_qr_scanned',
  onboarding_qr_closed_manually: 'onboarding_qr_closed_manually',
  header_create_new_org_click: 'header_create_new_org_click',

  integrations_modal_connect_click: 'integrations_modal_connect_click',

  // link creator
  video_transcoding_started: 'video_transcoding_started',
  video_transcoding_finished: 'video_transcoding_finished',
  video_transcoding_failed: 'video_transcoding_failed',

  // whatsapp_pay
  whatsapp_pay_connect: 'whatsapp_pay_connect',
  whatsapp_pay_disconnect: 'whatsapp_pay_disconnect',
  whatsapp_pay_cancel: 'whatsapp_pay_cancel',

  // chat
  chat_send_message_failure: 'chat_send_message_failure',

  // enigmaSales
  shipping_duplicate_name_found: 'shipping_duplicate_name_found',
  payment_duplicate_name_found: 'payment_duplicate_name_found',

  // whatsapp_connect
  whatsapp_connect_success_page_call: 'whatsapp_connect_success_page_call',
  whatsapp_connect_callback_received: 'whatsapp_connect_callback_received',
  whatsapp_connect_api_call: 'whatsapp_connect_api_call',

  //billing plan change
  update_subscription_plan: 'update_subscription_plan',
  billing_upgrade_click: 'billing_upgrade_click',
  billing_lite_upgrade_intent: 'billing_lite_upgrade_intent',
  billing_lite_upgrade_cancel: 'billing_lite_upgrade_cancel',
  billing_stripe_checkout_initiated: 'billing_stripe_checkout_initiated',
  billing_stripe_success_page_reached: 'billing_stripe_success_page_reached',

  //connect new catalog
  promote_catalog_shown: 'promote_catalog_shown',
  promote_catalog_close: 'promote_catalog_close',
  promote_catalog_click: 'promote_catalog_click',

  //apps
  app_click: 'app_click',

  //hallo bot language selection.
  bot_foreign_language_selection: 'bot_foreign_language_selection',
  app_search_focus: 'app_search_focus',
  app_card_click: 'app_card_click',

  //sign up flow
  onboarding_signup_cta: 'onboarding_signup_cta',
  onboarding_join_cta: 'onboarding_join_cta',
  onboarding_create_account_cta: 'onboarding_create_account_cta',
  onboarding_connect_whatsapp: 'onboarding_connect_whatsapp',
  onboarding_connect_success: 'onboarding_connect_success',

  // guru suggestion
  guru_suggestion_click: 'guru_suggestion_click',
  guru_suggestion_send_click: 'guru_suggestion_send_click',
  guru_suggestion_edit_click: 'guru_suggestion_edit_click',
  guru_suggestion_close_click: 'guru_suggestion_close_click',
  track_reditus: 'track_reditus',

  //broadcast_testing
  create_new_broadcast: 'create_new_broadcast',

  //store_switcher
  store_switch_no_access: 'store_switch_no_access',

  // catalogManager
  catalog_app_banner_click: 'catalog_app_banner_click',
  catalog_app_publish_check: 'catalog_app_publish_check',
  catalog_app_save_button_click: 'catalog_app_save_button_click',
  catalog_app_save_next_click: 'catalog_app_save_next_click',

  //dock catalog add to cart
  catalog_on_add_to_cart_click: 'catalog_on_add_to_cart_click',
  catalog_on_add_to_cart_button_click: 'catalog_on_add_to_cart_button_click',
  catalog_on_add_to_cart_api_call_start:
    'catalog_on_add_to_cart_api_call_start',
  catalog_on_add_to_cart_api_call_end: 'catalog_on_add_to_cart_api_call_end',
  catalog_on_cart_item_edit: 'catalog_on_cart_item_edit',
  catalog_on_clear_cart: 'catalog_on_clear_cart',

  //lite plan
  facebook_billing_setup_click: 'facebook_billing_setup_click',

  //spam template
  spam_template_create: 'spam_template_create',

  // quick_find
  quick_find_tab_change: 'quick_find_tab_change',
  quick_find_filter_change: 'quick_find_filter_change',
  quick_find_clear_click: 'quick_find_clear_click',
  quick_find_search: 'quick_find_search',
  quick_find_hide_closed_chats: 'quick_find_hide_closed_chats',

  //billing
  billing_phone_pe_checkout_cancelled: 'billing_phone_pe_checkout_cancelled',
  billing_phone_pe_checkout_concluded: 'billing_phone_pe_checkout_concluded',
  billing_phone_pe_checkout_initiated: 'billing_phone_pe_checkout_initiated',

  //flowhippo
  flowhippo_billing_plan_card_click: 'flowhippo_billing_plan_card_click',
  flowhippo_billing_phone_pe_pending_timeout:
    'flowhippo_billing_phone_pe_pending_timeout',
  flowhippo_billing_payment_received: 'flowhippo_billing_payment_received',
  // header
  header_profile_click: 'header_profile_click',
  header_switch_org_click: 'header_switch_org_click',
  header_org_settings_link_click: 'header_org_settings_link_click',
  header_personal_link_click: 'header_personal_link_click',
  header_sign_out_click: 'header_sign_out_click',
  header_logo_click: 'header_logo_click',
  header_tab_change: 'header_tab_change',

  // flowLibrary
  flowLibrary_create_new_flow_click: 'flowLibrary_create_new_flow_click',
  flowLibrary_tab_change: 'flowLibrary_tab_change',
  flowLibrary_card_click: 'flowLibrary_card_click',
  flowLibrary_flow_edit_tab: 'flowLibrary_flow_edit_tab',
  flowLibrary_flow_snapshot_click: 'flowLibrary_flow_snapshot_click',
  flowLibrary_header_publish_click: 'flowLibrary_header_publish_click',
  flowLibrary_header_use_playbook_click:
    'flowLibrary_header_use_playbook_click',
  flowLibrary_header_close_click: 'flowLibrary_header_close_click',
  flowLibrary_header_back_click: 'flowLibrary_header_back_click',

  // flowCanvas
  flowCanvas_header_publish_click: 'flowCanvas_header_publish_click',
  flowCanvas_header_close_click: 'flowCanvas_header_close_click',
  flowCanvas_header_back_click: 'flowCanvas_header_back_click',
  flowCanvas_header_use_playbook_click: 'flowCanvas_header_use_playbook_click',
  flowCanvas_header_delete_click: 'flowCanvas_header_delete_click',
  flowCanvas_footer_save_click: 'flowCanvas_footer_save_click',
  flowCanvas_footer_close_click: 'flowCanvas_footer_close_click',
  flowCanvas_free_to_paid_plan_nudge: 'flowCanvas_free_to_paid_plan_nudge',
  flowCanvas_publish_no_credits: 'flowCanvas_publish_no_credits',

  // integrations
  integrations_card_connect_click: 'integrations_card_connect_click',
  integrations_card_delete_click: 'integrations_card_delete_click',
  integrations_card_edit_click: 'integrations_card_edit_click',
  flows_integrations_modal_connect_click:
    'flows_integrations_modal_connect_click',

  // usageLogs

  usage_logs_export_confirm_click: 'usage_logs_export_confirm_click',

  // billing
  billing_phone_pe_pending_timeout: 'billing_phone_pe_pending_timeout',
  billing_pay_now_click: 'billing_pay_now_click',
  billing_payment_callback: 'billing_payment_callback',
  billing_payment_received: 'billing_payment_received',
  billing_plan_card_click: 'billing_plan_card_click',

  flows_billing_phone_pe_checkout_initiated:
    'flows_billing_phone_pe_checkout_initiated',
  flows_billing_phone_pe_checkout_concluded:
    'flows_billing_phone_pe_checkout_concluded',
  flows_billing_phone_pe_checkout_cancelled:
    'flows_billing_phone_pe_checkout_cancelled',
  //signup lead
  signup_lead: 'signup_lead',

  //2fa
  two_fa_setup_success: 'two_fa_setup_success',
  mfa_login_request_otp: 'mfa_login_request_otp',
  mfa_enroll_request_otp: 'mfa_enroll_request_otp',
  mfa_enroll_error: 'mfa_enroll_error',
  mfa_login_error: 'mfa_login_error',

  //shopify welcome prompt
  shopify_welcome_prompt_whatsapp_click:
    'shopify_welcome_prompt_whatsapp_click',
  already_have_account_click: 'already_have_account_click',
  show_shopify_welcome_prompt: 'show_shopify_welcome_prompt',
  shopify_welcome_prompt_email_click: 'shopify_welcome_prompt_email_click',
  reload_lazy_click: 'reload_lazy_click',
  shopify_welcome_unable_to_scan_submitted:
    'shopify_welcome_unable_to_scan_submitted',

  // free plan discontinuation
  free_plan_discontinuation_show: 'free_plan_discontinuation_show',
  free_plan_discontinuation_accept: 'free_plan_discontinuation_accept',

  // chatv4 announcement
  chatv4_announcement_show: 'chatv4_announcement_show',
  chatv4_announcement_accept: 'chatv4_announcement_accept',
  chatv4_announcement_do_not_show_again:
    'chatv4_announcement_do_not_show_again',
  chatv4_announcement_contact_support: 'chatv4_announcement_contact_support',
  //recommended flows modal
  recommended_flows_modal_open: 'recommended_flows_modal_open',
  recommended_flows_modal_publish_click:
    'recommended_flows_modal_publish_click',
  recommended_flows_modal_closed: 'recommended_flows_modal_closed',

  //Navbar
  add_inbox_click: 'add_inbox_click',
  add_inbox_folder_click: 'add_inbox_folder_click',

  //quickfind
  inbox_created: 'inbox_created',
  quick_find_hide_closed_chats: 'quick_find_hide_closed_chats',

  //realtime
  realtime_subscription_missed: 'realtime_subscription_missed',
  realtime_subscription_calls: 'realtime_subscription_calls',

  //referral offer
  referral_offer_show: 'referral_offer_show',
  referral_offer_accept: 'referral_offer_accept',
  referral_offer_do_not_show_again: 'referral_offer_do_not_show_again',
  track_messages: 'track_messages',

  //referral offer
  mobile_app_2025_show: 'mobile_app_2025_show',
  mobile_app_2025_accept: 'mobile_app_2025_accept',
  mobile_app_2025_do_not_show_again: 'mobile_app_2025_do_not_show_again',

  // insta 2025 pricing
  insta_2025_pricing_show: 'insta_2025_pricing_show',
  insta_2025_pricing_accept: 'insta_2025_pricing_accept',
  insta_2025_pricing_do_not_show_again: 'insta_2025_pricing_do_not_show_again',
  insta_2025_pricing_contact_support: 'insta_2025_pricing_contact_support',

  //contacts
  export_contacts: 'export_contacts',

  //billing-modal
  shopify_migration_warning_modal_shown:
    'shopify_migration_warning_modal_shown',
  shopify_migration_agreement_click: 'shopify_migration_agreement_click',
  shopify_migration_reset_success: 'shopify_migration_reset_success',
  shopify_migration_reset_failure: 'shopify_migration_reset_failure',
  shopify_migration_reset_failure_catch:
    'shopify_migration_reset_failure_catch',
  shopify_migration_credits_migrate_failure:
    'shopify_migration_credits_migrate_failure',
  shopify_migration_credits_migrate_success:
    'shopify_migration_credits_migrate_success',

  //realtime_verification
  my_chats_count_realtime_verification: 'my_chats_count_realtime_verification',
  my_chats_count_mismatch: 'my_chats_count_mismatch',
  my_chats_tabs_change_counts: 'my_chats_tabs_change_counts',
  mqtt_reconnect: 'mqtt_reconnect',
  pinned_chats_overflow: 'pinned_chats_overflow',
  conversations_same_timestamp: 'conversations_same_timestamp',
  conv_subscription_missing3: 'conv_subscription_missing3',
  realtime_invalid_topic_subscription: 'realtime_invalid_topic_subscription',
  retry_conv_list_click: 'retry_conv_list_click',
  retry_messages_click: 'retry_messages_click',

  //Navbar
  navbar_search_icon_click: 'navbar_search_icon_click',
  navbar_start_new_chat_click: 'navbar_start_new_chat_click',
  navbar_my_chats_click: 'navbar_my_chats_click',
  navbar_all_chats_click: 'navbar_all_chats_click',
  navbar_unassigned_chats_click: 'navbar_unassigned_chats_click',
  navbar_change_agent_click: 'navbar_change_agent_click',
  navbar_ai_automation_click: 'navbar_ai_automation_click',
  navbar_analytics_click: 'navbar_analytics_click',
  navbar_whatsapp_click: 'navbar_whatsapp_click',
  navbar_shopify_click: 'navbar_shopify_click',
  navbar_contacts_click: 'navbar_contacts_click',
  navbar_profile_security_click: 'navbar_profile_security_click',
  navbar_more_click: 'navbar_more_click',
  navbar_account_settings_click: 'navbar_account_settings_click',

  apply_default_templates: 'apply_default_templates',
  voice_message_sent: 'voice_message_sent',
  disable_bulk_2fa: 'disable_bulk_2fa',
  instagram_comment_sent: 'instagram_comment_sent',
  instagram_reply_via_dm: 'instagram_reply_via_dm',

  session_blocker_continue_here_click: 'session_blocker_continue_here_click',
  session_blocker_logout_click: 'session_blocker_logout_click',
  session_blocker_viewed: 'session_blocker_viewed',

  instagram_in_flow_launch_show: 'instagram_in_flow_launch_show',
  instagram_in_flow_launch_learn_more_click:
    'instagram_in_flow_launch_learn_more_click',
  instagram_in_flow_launch_not_interested_click:
    'instagram_in_flow_launch_not_interested_click',

  // shopify welcome prompt
  shopify_welcome_show: 'shopify_welcome_show',
  shopify_welcome_unable_to_scan_click: 'shopify_welcome_unable_to_scan_click',
  shopify_welcome_contact_no_submit_click:
    'shopify_welcome_contact_no_submit_click',
  shopify_welcome_contact_no_back_click:
    'shopify_welcome_contact_no_back_click',
  shopify_welcome_contact_submit_back_click:
    'shopify_welcome_contact_submit_back_click',
  shopify_welcome_prompt_widget_click: 'shopify_welcome_prompt_widget_click',

  instagram_in_flow_launch_webhook_show:
    'instagram_in_flow_launch_webhook_show',
  instagram_in_flow_launch_webhook_learn_more_click:
    'instagram_in_flow_launch_webhook_learn_more_click',
  instagram_in_flow_launch_webhook_not_interested_click:
    'instagram_in_flow_launch_webhook_not_interested_click',

  insta_flow_2025_sept_show: 'insta_flow_2025_sept_show',
  insta_flow_2025_sept_accept: 'insta_flow_2025_sept_accept',
  insta_flow_2025_sept_stop: 'insta_flow_2025_sept_stop',
  insta_flow_2025_sept_video_click: 'insta_flow_2025_sept_video_click',

  shopify_billing_currency_mismatch: 'shopify_billing_currency_mismatch',

  // signup churn tracking
  churn_login_visit: 'churn_login_visit',
  churn_login_security: 'churn_login_security',
  churn_login_flow: 'churn_login_flow',
  churn_signup_flow: 'churn_signup_flow',
  churn_verify_phone_submit: 'churn_verify_phone_submit',
  churn_create_account_visit: 'churn_create_account_visit',
  churn_meta_connect_visit: 'churn_meta_connect_visit',
  churn_meta_connect_success: 'churn_meta_connect_success',
  churn_qr_code_shown: 'churn_qr_code_shown',
  churn_qr_code_accepted: 'churn_qr_code_accepted',

  // meta quick onboarding
  mqo_migration_visit: 'mqo_migration_visit',
  mqo_migration_select: 'mqo_migration_select',

  mqo_request_verification_request: 'mqo_request_verification_request',
  mqo_request_verification_success: 'mqo_request_verification_success',
  mqo_request_verification_error: 'mqo_request_verification_error',

  mqo_otp_resend_request: 'mqo_otp_resend_request',
  mqo_otp_resend_success: 'mqo_otp_resend_success',
  mqo_otp_resend_error: 'mqo_otp_resend_error',

  mqo_otp_verification_request: 'mqo_otp_verification_request',
  mqo_otp_verification_success: 'mqo_otp_verification_success',
  mqo_otp_verification_error: 'mqo_otp_verification_error',

  mqo_free_number_request: 'mqo_free_number_request',

  mqo_website_verification_request: 'mqo_website_verification_request',

  mqo_whatsapp_display_name_validation_request:
    'mqo_whatsapp_display_name_validation_request',

  mqo_meta_connect_request: 'mqo_meta_connect_request',
  mqo_meta_connect_success: 'mqo_meta_connect_success',
  mqo_meta_connect_error: 'mqo_meta_connect_error',

  mqo_meta_connect_cloud_request: 'mqo_meta_connect_cloud_request',
  mqo_meta_connect_cloud_success: 'mqo_meta_connect_cloud_success',
  mqo_meta_connect_cloud_error: 'mqo_meta_connect_cloud_error',

  mqo_whatsapp_display_name_guidelines_click:
    'mqo_whatsapp_display_name_guidelines_click',

  mqo_delete_whatsapp_account_click: 'mqo_delete_whatsapp_account_click',
  mqo_disable_2fa_for_whatsapp_api_click:
    'mqo_disable_2fa_for_whatsapp_api_click',
  mqo_lead: 'mqo_lead',
  mqo_fb_object_setup: 'mqo_fb_object_setup',
  mqo_save_lead_error: 'mqo_save_lead_error',
  mqo_website_field_load: 'mqo_website_field_load',
  mqo_meta_payment_status_modal_load: 'mqo_meta_payment_status_modal_load',

  mqo_business_phone_duplicate_error: 'mqo_business_phone_duplicate_error',
  mqo_business_phone_duplicate_error_contact_us_click:
    'mqo_business_phone_duplicate_error_contact_us_click',
  mqo_business_phone_duplicate_error_go_back_to_login_click:
    'mqo_business_phone_duplicate_error_go_back_to_login_click',
  mqo_business_phone_duplicate_error_close_click:
    'mqo_business_phone_duplicate_error_close_click',
  mqo_session_logs: 'mqo_session_logs',

  whatsapp_integration_blocker_click: 'whatsapp_integration_blocker_click',
  maintenance_announcement_close: 'maintenance_announcement_close',
  maintenance_announcement_show: 'maintenance_announcement_show',
  suggestion_action: 'suggestion_action',

  // hub
  hub_visited: 'hub_visited',
  sidebar_click: 'sidebar_click',
  revenue_table_tab_change: 'revenue_table_tab_change',
  date_range_change: 'date_range_change',

  hub_announcement_show: 'hub_announcement_show',
  hub_announcement_accept: 'hub_announcement_accept',
  hub_announcement_remind_later: 'hub_announcement_remind_later',
}
trackPH.events = trackEvent.events

export default trackEvent
